import { useState } from 'react';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Plus, Search, Users, Globe, Upload } from 'lucide-react';
import { useContactStore } from '@/stores/contactStore.js';
import { ContactItem } from '@/components/contacts/ContactItem.js';
import { AddContactDialog } from '@/components/contacts/AddContactDialog.js';
import { DiscoverServicesDialog } from '@/components/contacts/DiscoverServicesDialog.js';
import { PublishAgentDialog } from '@/components/contacts/PublishAgentDialog.js';

interface ContactsPanelProps {
    onSelect?: () => void;
}

export function ContactsPanel({ onSelect: _onSelect }: ContactsPanelProps) {
    const [searchQuery, setSearchQuery] = useState('');
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showDiscoverDialog, setShowDiscoverDialog] = useState(false);
    const [showPublishDialog, setShowPublishDialog] = useState(false);
    const searchContacts = useContactStore((state) => state.searchContacts);

    const contacts = searchContacts(searchQuery);

    return (
        <>
            <div className="flex flex-col h-full">
                {/* Header - hidden on mobile since MobileSheet has its own */}
                <div className="p-4 border-b border-border/50 hidden md:block">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-lg font-semibold">Contacts</h2>
                        <div className="flex gap-2">
                            <Button
                                size="icon"
                                variant="outline"
                                onClick={() => setShowDiscoverDialog(true)}
                                className="h-8 w-8"
                                title="Discover Agents"
                            >
                                <Globe className="h-4 w-4" />
                            </Button>
                            <Button
                                size="icon"
                                variant="outline"
                                onClick={() => setShowPublishDialog(true)}
                                className="h-8 w-8"
                                title="Publish Your Assistant"
                            >
                                <Upload className="h-4 w-4" />
                            </Button>
                            <Button
                                size="icon"
                                variant="default"
                                onClick={() => setShowAddDialog(true)}
                                className="h-8 w-8 bg-primary hover:bg-primary/90"
                                title="Add Contact"
                            >
                                <Plus className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>

                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search contacts..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9 h-9"
                        />
                    </div>
                </div>

                {/* Mobile header with search */}
                <div className="p-4 md:hidden">
                    <div className="flex gap-2 mb-3">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search contacts..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 h-10"
                            />
                        </div>
                        <Button
                            size="icon"
                            variant="outline"
                            onClick={() => setShowDiscoverDialog(true)}
                            className="h-10 w-10"
                            title="Discover Agents"
                        >
                            <Globe className="h-5 w-5" />
                        </Button>
                        <Button
                            size="icon"
                            variant="outline"
                            onClick={() => setShowPublishDialog(true)}
                            className="h-10 w-10"
                            title="Publish Your Assistant"
                        >
                            <Upload className="h-5 w-5" />
                        </Button>
                        <Button
                            size="icon"
                            variant="default"
                            onClick={() => setShowAddDialog(true)}
                            className="h-10 w-10 bg-primary hover:bg-primary/90"
                            title="Add Contact"
                        >
                            <Plus className="h-5 w-5" />
                        </Button>
                    </div>
                </div>

                {/* Contacts List */}
                <div className="flex-1 overflow-y-auto">
                    {contacts.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                            <Users className="h-12 w-12 text-muted-foreground/50 mb-3" />
                            <p className="text-sm text-muted-foreground mb-4">
                                {searchQuery
                                    ? 'No contacts match your search'
                                    : 'No contacts yet'}
                            </p>
                            {!searchQuery && (
                                <div className="flex gap-2">
                                    <Button
                                        size="sm"
                                        onClick={() => setShowDiscoverDialog(true)}
                                        className="gap-2"
                                        variant="outline"
                                    >
                                        <Globe className="h-4 w-4" />
                                        Discover Agents
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={() => setShowAddDialog(true)}
                                        className="gap-2"
                                    >
                                        <Plus className="h-4 w-4" />
                                        Add Contact
                                    </Button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="p-4 space-y-2">
                            {contacts.map((contact) => (
                                <ContactItem key={contact.id} contact={contact} />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Dialogs */}
            <AddContactDialog open={showAddDialog} onOpenChange={setShowAddDialog} />
            <DiscoverServicesDialog open={showDiscoverDialog} onOpenChange={setShowDiscoverDialog} />
            <PublishAgentDialog open={showPublishDialog} onOpenChange={setShowPublishDialog} />
        </>
    );
}
